<script lang="ts" context="module">
  import { Observable, switchMap, combineLatest, take, of } from "rxjs";
  import { catchError, debounceTime, delay, map, tap } from "rxjs/operators";
  import { create, test, enforce, type SuiteRunResult } from "vest";

  import type { Address } from "proto/address";
  import type { User } from "proto/user";
  import { put$, profile$, refreshProfile } from "$lib/serv/core/auth";
  import { RxWritable } from "$lib/util/obs";
  import { rexAlphaNumPuncExt, rexNum } from "$lib/util/form";

  export type AddressFormContext = "general" | "profile" | "checkout";

  type State = {
    address: Address,
    submitted: boolean,
    saving: boolean,
    context: AddressFormContext,
  }

  const state$ = new RxWritable<State>({
    address: xaddress().toPlain(),
    submitted: false,
    saving: false,
    context: "general"
  });

  export const address$: Observable<Address> = state$.pipe(map((s) => s.address));
  export const savingAddress$: Observable<boolean> = state$.pipe(map((s) => s.saving));

  const validation$: Observable<SuiteRunResult<string, string> | null> = state$.pipe(
    map((state) => {
      const { address, submitted, context } = state;
      if (!submitted) return null;

      return create((vAddress: Address) => {
        test("building_type", "Building type is required", () => {
          enforce(vAddress.building_type).isNotBlank();
        });

        if (requiresComplex(vAddress.building_type)) {
          const xLbl = getComplexPlaceholder(vAddress.building_type);
          test("complex", `${xLbl} is required`, () => {
            enforce(vAddress.complex).isNotBlank();
          });
          test("complex", `${xLbl} contain invalid characters`, () => {
            enforce(vAddress.complex).matches(rexAlphaNumPuncExt);
          });
        }

        // allow insane values for street_number on checkout, due to weirdness / inconsistencies in the PROD DB
        if (context === "checkout") {
          test("street_number", "Street number is invalid", () => {
            const insane = /^([\n\p{L}0-9 ()&.,;:_-]*)$/iu;
            enforce(vAddress.street_number).matches(insane);
          });
        }

        if (context !== "checkout") {
          test("street_number", "Street number is required", () => {
            enforce(vAddress.street_number).isNotBlank();
            enforce(truthy(vAddress.street_number)).equals(true);
          });

          test("street_number", "Street number is invalid", () => {
            enforce(vAddress.street_number).matches(rexNum);
            enforce(Number(vAddress.street_number)).gt(0);
          });
        }

        if (vAddress.saved_delivery_instruction) {
          test("saved_delivery_instruction", "Instructions contain invalid characters", () => {
            enforce(vAddress.saved_delivery_instruction).matches(rexAlphaNumPuncExt);
          });
        }

        // test("label", "Label is required", () => {
        //   enforce(data.label).isNotBlank();
        // });

        // test("other", "Address is not totaly complete", () => {
        //   enforce(data).condition((a) => getCompletion(a) === "total");
        // });
      })(address);
    })
  );

  export function saveAddress(): Observable<Address> {
    state$.update((s) => {
      s.submitted = true;
      s.saving = true;
      return s;
    });
    const save$: Observable<Address> = combineLatest([state$, validation$, profile$]).pipe(
      take(1),
      // delay(1000),
      switchMap(([state, validation, user]) => {
        const { address, context } = state;
        if (!user || validation?.hasErrors()) {
          state$.update((s) => {
            s.saving = false;
            return s;
          });
          throw new Error(!user ? "Not logged in" : `Address is invalid ${validation?.errors}`);
        }
        if (falsy(address.label)) {
          return of(address)
        }

        const saved_addresses = user.saved_addresses || [];
        let i = saved_addresses.findIndex((a) => a.label === address.label);
        const savedAddr = i !== -1;

        i = savedAddr ? i : saved_addresses.length;
        saved_addresses[i] = !savedAddr || context !== "checkout" ? address : xaddress(address).toChangeable() as Address;

        // console.log("save address ---> ", address);
        // return of(address);

        return put$<User>(`api://v3/users/${user.id}`, { saved_addresses }).pipe(
          map(({ saved_addresses }) => {
            refreshProfile();
            return saved_addresses[i];
          }),
          catchError((err) => {
            state$.update((s) => {
              s.saving = false;
              return s;
            });
            throw new Error(err?.error?.friendly_message || "Failed to save address");
          })
        );
      }),
      tap(() => state$.update((s) => {
        s.saving = false;
        return s;
      }))
    );

    return save$;
  }
</script>

<script lang="ts">
  import { onMount } from "svelte";
  import { load, promiseGeoAddress } from "$lib/serv/vendor-integration/google-maps";
  import { dissectGooglePlaceAddress } from "$lib/serv/locations";
  import {
    addressLabels,
    requiresComplex,
    getComplexPlaceholder,
    buildingTypes,
    getCompletion,
    xaddress,
  } from "$lib/util/address";
  import Textarea from "$lib/ui/form/Textarea.svelte";
  import Input from "$lib/ui/form/Input.svelte";
  import {falsy, truthy} from "$lib/util/func";


  export let address: Address;
  export let showMap = false;
  export let context: AddressFormContext = "general";

  state$.update((s) => {
    s.address = xaddress(address).toPlain();
    s.submitted = false;
    s.context = context;
    return s;
  });

  let gMap: google.maps.Map;
  // TODO: Update so that we use the recommended AdvancedMarkerElement, not the legacy Marker.
  // There is an example of this on driver tracking.
  let gMarker: google.maps.Marker;
  let divMap: HTMLDivElement;

  onMount(async () => {
    await load();
    // console.log({ address, complete: AddressCompletion.get(address).value });

    const coords = { lat: Number(address.latitude), lng: Number(address.longitude) };
    gMap = new google.maps.Map(divMap, {
      center: coords,
      styles: [
        {
          featureType: "all",
          elementType: "labels",
          stylers: [{ visibility: "on" }],
        },
      ],
      streetViewControl: false,
      mapTypeControl: false,
      scrollwheel: false,
      zoom: 17,
    });

    gMap.setCenter(coords);

    gMarker = new google.maps.Marker({
      map: gMap,
      position: coords,
      // NB: don't delete
      // draggable: true,
    });

    gMarker.setPosition(coords);

    // NB: don't delete
    // gMarker.addListener("dragend", (e: Record<string, any>) => {
    //   const coords = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    //   setPosition(coords);
    // });

    // ignore icomplete saved addresses
    if (address.label) {
      return;
    }

    // NB: don't delete, maybe
    // polyfill address based on coords
    // if (getCompletion(address) === "geo") setPosition(coords);
  });

  let lastGoodCoords: google.maps.LatLngLiteral = {
    lat: Number(address.latitude),
    lng: Number(address.longitude),
  };
  async function setPosition(coords: google.maps.LatLngLiteral) {
    const results = await promiseGeoAddress(coords.lat, coords.lng);
    if (!results || results.length === 0) {
      console.log("NO RESULTS");
      return;
    }

    const loc = results[0];
    const COMPLETE_ADDRESS = 8;
    if (loc.address_components.length < COMPLETE_ADDRESS) {
      console.log(`INCOMPLETE ADDRESS`);
      return;
    }

    const addr = xaddress({
      // main
      latitude: coords.lat.toString(),
      longitude: coords.lng.toString(),
      ...dissectGooglePlaceAddress(loc.address_components),
      // meta
      building_type: address.building_type,
      complex: address.complex,
      label: address.label,
      saved_delivery_instruction: address.saved_delivery_instruction,
    }).toPlain();

    const invalidStreetNumber = isNaN(Number(addr.street_number));
    if (invalidStreetNumber) {
      console.log(`NaN ${addr.street_number}`);
      return;
    }

    if ($address$.street_name && $address$.street_name.toLowerCase() !== addr.street_name.toLowerCase()) {
      console.log("NOT SAME STREET");
      gMarker.setPosition(lastGoodCoords);
      return;
    }

    lastGoodCoords = coords;
    gMarker.setPosition(coords);
    // address$.set(addr);
    state$.update((s) => {
      s.address = addr;
      return s;
    });
  }

  const errorScroll$ = combineLatest([state$, validation$]).pipe(
    debounceTime(500),
    tap(([state, validation]) => {
      const { submitted } = state;
      if (!submitted || !validation) return;

      const props = ["building_type", "complex", "street_number", "saved_delivery_instruction", "label"];
      for (const p of props) {
        if (validation?.hasErrors(p)) {
          const el = document.getElementById(`${p}-div`);
					el?.scrollIntoView({ behavior: 'smooth' });
          return;
        }
      }
    }),
  );
  $errorScroll$;
  // this may need some tweaks. reactivity
  const savedAddr: boolean =  truthy($profile$?.saved_addresses?.find(a => a.label === $address$.label));
</script>

<div id="map" bind:this={divMap} hidden={!showMap} />
<form on:submit|preventDefault>
  <section>
    <h2 id="loc-info" class="headline-s bold"><span class="icon-pin-outline-28" />
      <span>{savedAddr ? "Update" : "Add"} delivery location info</span>
    </h2>
    
    <div id="building_type-div" class="input-set">
      <div class="pills">
        {#each buildingTypes as { label, value }}
          <label class:error={$validation$?.hasErrors("building_type")}>
            <input
              bind:group={$state$.address.building_type}
              id={label}
              {value}
              name="addressType"
              type="radio"
              hidden
            />
            <span>{label}</span>
          </label>
        {/each}
      </div>
      {#if $validation$?.hasErrors("building_type")}
        <span class="error addressErr">{$validation$?.getErrors("building_type")[0]}</span>
      {/if}
    </div>

    {#if requiresComplex($state$.address.building_type)}
      <div id="complex-div" class="input-set">
        <label class="label-xl book" for="complex">
          <input
            bind:value={$state$.address.complex}
            id="complex"
            name="complex"
            placeholder=" "
            type="text"
            class:error={$validation$?.hasErrors("complex")}
            required
          />
          <span>{getComplexPlaceholder($state$.address.building_type)}</span>
        </label>
        {#if $validation$?.hasErrors("complex")}
          <span class="error">{$validation$?.getErrors("complex")[0]}</span>
        {/if}
      </div>
    {/if}

    <div id="street_number-div" class="input-set">
      <Input
        bind:value={$state$.address.street_number}
        label="street number"
        name="street_number"
        placeholder=" "
        type="text"
        validityClass={$validation$?.hasErrors("street_number") ? 'error' : ''}
        required />
      {#if $validation$?.hasErrors("street_number")}
        <span class="error">{$validation$?.getErrors("street_number")[0]}</span>
      {:else}
        <span>{xaddress($state$.address).remove("province", "postal_code").toStr()}</span>
      {/if}
    </div>

  </section>

  <section>
    <h2 class="headline-s bold margin-bottom-6" style="margin-bottom: 6px;">{savedAddr ? "Update" : "Add"} delivery instructions</h2>
    <div id="saved_delivery_instruction-div" class="input-set">
      <div class="bolt label-xl">
        <span class="icon-bolt-16" />
        This helps us find you faster
      </div>
      <Textarea
        bind:value={$state$.address.saved_delivery_instruction}
        label={`Add delivery instructions here`}
        name="instructions"
        placeholder=" "
        validityClass={$validation$?.hasErrors("saved_delivery_instruction") ? 'error' : ''}
      />
      {#if $validation$?.hasErrors("saved_delivery_instruction")}
        <span class="error">{$validation$?.getErrors("saved_delivery_instruction")[0]}</span>
      {/if}
    </div>
  </section>

  {#if !savedAddr || context !== "checkout" }
    <section>
      <h2 class="headline-s bold">Save address as</h2>
      <div id="label-div" class="input-set">
        <div class="pills">
          {#each (context === "checkout" ? addressLabels : addressLabels.filter(label => label.value !== "")) as { label, value }}
            <label class:error={$validation$?.hasErrors("label")}>
              <input bind:group={$state$.address.label} id={label} {value} name="addressTag" type="radio" hidden />
              <span>{label}</span>
            </label>
          {/each}
        </div>
        {#if $validation$?.hasErrors("label")}
          <span class="error addressErr">{$validation$?.getErrors("label")[0]}</span>
        {/if}
      </div>
    </section>
  {/if}
</form>

<style lang="scss">
  @use "color";
  @use "screen";

  #map {
    border: 1px solid color.$separator;
    border-radius: 25px;
    height: 300px;
    margin-bottom: 42px;
  }

  form {
    padding-left: 0;
    @media (screen.$md) { padding-left: 50px; }
  }

  h2#loc-info {
    position: relative;
    display: flex;
    align-items: center;

    span {
      &:nth-of-type(1) {
        color: color.$on-background-medium-importance;
        margin-right: 24px;
        @media(screen.$md) {
          position: absolute;
          left: -47px;
        }
      }
      // position: absolute;
      // left: -50px;
      // color: color.$on-background-medium-importance;
      // transform: scale(1);

      // @media (screen.$max-sm) {
      //   position: static;
      //   margin-right: 42px;
      // }
    }
  }

  .bolt {
    margin-bottom: 1.2rem;
    span { color: color.$primary-variant; } 
  }

  .addressErr {
    position: absolute;
    top: 100%;
  }
</style>
